import _ from 'lodash';
import React, { Component } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Row, Col, FormGroup, ControlLabel, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import ReactTable from 'react-table';
import Button from './Button';
import ReactTablePagination from './ReactTablePagination';
import { caseSetServerError } from '../../actions/caseActions';
import { isEmptyString } from './../../validators/formValidators';
import { colorMap, dialogHeaderFooterBgColor } from '../../helpers/colors';
import { findBootstrapEnvironment } from '../../helpers/utils';


class EditTypesDialog extends Component {
    constructor(props) {
        super(props);
        this.add = this.add.bind(this);
        this.onAdd = this.onAdd.bind(this);
        this.onSave = this.onSave.bind(this);
        this.onClone = this.onClone.bind(this);
        this.onClose = this.onClose.bind(this);
        this.onDelete = this.onDelete.bind(this);
        this.moveSelectedTypeUp = this.moveSelectedTypeUp.bind(this);
        this.moveSelectedTypeDown = this.moveSelectedTypeDown.bind(this);
        this.renderEditable = this.renderEditable.bind(this);
        this.removeButton = this.removeButton.bind(this);
        this.cloneButton = this.cloneButton.bind(this);
        this.tableRef = React.createRef();
        this.state = {
            types: [],
            selectedIndex: -1,
            page: 0,
        };
    }

    componentWillReceiveProps(nextProps) {
        let types = _.cloneDeep(nextProps.types);
        if (types) {
            for (let i = 0; i < types.length; i++) {
                types[i]['$Type$'] = types[i].type;
            }

            this.setState({types});
            if (nextProps.defaultSelectedIndex >= 0 && nextProps.defaultSelectedIndex < types.length) {
                let page = 0;
                if (this.tableRef.current) {
                    page = parseInt((nextProps.defaultSelectedIndex - 1) / this.tableRef.current.state.pageSize, 10);
                }

                this.setState({
                    selectedIndex: nextProps.defaultSelectedIndex,
                    page,
                });

            } else {
                this.setState({
                    selectedIndex: -1,
                    page: 0,
                });
            }
        }
    }

    onSave() {
        const types = _.cloneDeep(this.state.types);
        for (let i = 0; i < types.length; i++) {
            if (types[i]['$Error$']) {
                return;
            }

            types[i].type = types[i]['$Type$'].trim();
            delete types[i]['$Type$'];
        }

        if (this.props.onSave) {
            this.props.onSave(this.state.selectedIndex, types);
            return;
        }

        if (this.props.dispatchSaveAction) {
            this.props.dispatch(this.props.dispatchSaveAction(types))
                .then((result) => {
                    if (!result.data) {
                        this.props.dispatch(caseSetServerError('Failed to save modified types.'));
                        return;
                    }

                    this.props.onClose();
                }).catch((error) => {
                    this.props.dispatch(caseSetServerError(error));
                });
        }

        if (this.props.saveAction) {
            this.props.saveAction(types);
            this.props.onClose();
        }
    }

    onClose() {
        this.setState({
            types: this.props.types,
            selectedIndex: -1,
        });

        this.props.onClose();
    }

    onDelete(index) {
        if (index < 0) {
            return;
        }

        const types = _.cloneDeep(this.state.types);
        types.splice(index, 1);
        const page = parseInt((index - 1) / this.tableRef.current.state.pageSize, 10);
        this.setState({
            types,
            selectedIndex: this.state.selectedIndex === index ? index - 1 : this.state.selectedIndex,
            page,
        });
    }

    onAdd() {
        this.add(this.props.defaultType, 'New');
    }

    onClone(index) {
        if (index < 0) {
            return;
        }

        this.add(this.state.types[index],
                 this.state.types[index]['$Type$']);
    }

    add(defaultType = this.props.defaultType, newLabelBase = 'New') {
        for (let i = 1; ; i += 1) {
            const newTypeLabel = `${newLabelBase} ${i}`;
            if (this.state.types.find(types => types['$Type$'] === newTypeLabel)) {
                continue;
            }

            const newType = _.cloneDeep(defaultType);
            newType.type = newTypeLabel;
            newType['$Type$'] = newTypeLabel;
            const types = _.cloneDeep(this.state.types);
            types.push(newType);
            const page = parseInt((types.length - 1) / this.tableRef.current.state.pageSize, 10);
            this.setState({
                types,
                selectedIndex: types.length - 1,
                page,
            }, () => {
                // The new row is at the end of the list: scroll to it.
                const body = ReactDOM.findDOMNode(this.tableRef.current).querySelector('.rt-tbody');
                if (body) {
                    body.scrollTop = body.scrollHeight;
                }
            });

            break;
        }
    }

    moveSelectedTypeUp(evt) {
        const types = this.state.types;
        let selectedIndex = this.state.selectedIndex;
        if (selectedIndex < 0) {
            return;
        }

        const tmpHeader = types[selectedIndex - 1];
        types[selectedIndex - 1] = types[selectedIndex];
        types[selectedIndex] = tmpHeader;
        selectedIndex -= 1;
        this.setState({
            types,
            selectedIndex,
            page: parseInt(selectedIndex / this.tableRef.current.state.pageSize, 10),
        });
    }

    moveSelectedTypeDown(evt) {
        const types = this.state.types;
        let selectedIndex = this.state.selectedIndex;
        if (selectedIndex < 0) {
            return;
        }

        const tmpHeader = types[selectedIndex + 1];
        types[selectedIndex + 1] = types[selectedIndex];
        types[selectedIndex] = tmpHeader;
        selectedIndex += 1;
        this.setState({
            types,
            selectedIndex,
            page: parseInt(selectedIndex / this.tableRef.current.state.pageSize, 10),
        });
    }

    renderEditable(cellInfo) {
        const {
            enableEdit = true
        } = this.props;

        let selectedIndex = cellInfo.index;
        let modificationDisabled = this.props.modificationDisabled(this.state.types, selectedIndex) || !enableEdit;
        const validate = (types, selectedIndex, value = undefined) => {
            delete types[selectedIndex]['$Error$'];
            if (value === undefined) {
                value = types[selectedIndex]['$Type$'];
            }

            value = value && value.trim();
            if (value === '') {
                types[selectedIndex]['$Error$'] = 'Cannot be empty.';
                return;
            }

            for (let i = 0; i < types.length; i++) {
                if (selectedIndex === i) {
                    continue;
                }

                if (value === types[i]['$Type$'].trim()) {
                    types[selectedIndex]['$Error$'] = 'Duplicates are not allowed.';
                    return;
                }
            }

            if (['true', 'false', 'undefined', 'ALL'].includes(value)) {
                types[selectedIndex]['$Error$'] = 'Forbidden.';
                return;
            }

            if (!value.match(/^[A-Za-z0-9 ,-]*$/)) {
                types[selectedIndex]['$Error$'] = 'Invalid characters (only alphanumeric letters or \',- \' allowed).';
                return;
            }
        };

        const update = e => {
            if (modificationDisabled) {
                return;
            }

            const types = [...this.state.types];
            types[selectedIndex]['$Type$'] = e.target.value;
            validate(types, selectedIndex);
            this.setState({ types, selectedIndex });
        };

        let content = (
            <div onClick={() => this.setState({selectedIndex})}>
            <input type="text"
                   style={{ backgroundColor: "#fafafa", width: '100%', border: 'none' }}
                   disabled={modificationDisabled}
                   onChange={update}
                   value={this.state.types[selectedIndex]['$Type$']}
            />
            <div>{this.state.types[selectedIndex]['$Error$']}</div>
            </div>
        );

        return content;
    }

    removeButton(cellInfo) {
        const {
            enableDelete = true
        } = this.props;
        let modificationDisabled = this.props.modificationDisabled(this.state.types, cellInfo.index) || !enableDelete;
        return (
            <Button onClick={() => this.onDelete(cellInfo.index)}
                    disabled={modificationDisabled}
                    icon="trash"
                    style={{margin: -5}}
                    bsStyle="link"
                    tooltip="Delete selcted record"
            />);
    }

    cloneButton(cellInfo) {
        const {
            enableAdd = true
        } = this.props;

        return (
            <Button onClick={() => this.onClone(cellInfo.index)}                   
                    icon="duplicate"
                    style={{margin: -5}}
                    bsStyle="link"
                    disabled={!enableAdd}
                    tooltip="Duplicate (clone) selected record"
            />
        );
    }

    render() {
        const {
            enableAdd = true
        } = this.props;

        let saveDisabled = (!!this.state.types && this.state.types.find(v => (v && !isEmptyString(v['$Error$'])))) ||
                           this.props.loading ||
                           (!!this.props.onSave && this.state.selectedIndex < 0);

        const size = findBootstrapEnvironment();

        return (
            <Modal show={this.props.open}
                   onHide={this.props.onClose}
                   backdrop='static'
            >
                <Modal.Header closeButton style={{ backgroundColor: dialogHeaderFooterBgColor }}>
                    <Modal.Title>{this.props.label ? this.props.label : 'Edit'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                <Row>
                    <Col xs={9} md={10}>
                        <ReactTable PaginationComponent={ReactTablePagination}
                                    ref={this.tableRef}
                                    data={this.state.types}
                                    page={0}
                                    showPagination={false}
                                    pageSize={Math.max((this.state.types || []).length, 1)}
                                    columns={[
                                        {
                                            accessor: '$Type$',
                                            Cell: this.renderEditable,
                                        },
                                        {
                                            width: 50,
                                            Cell: this.cloneButton,
                                        },
                                        {
                                            width: 50,
                                            Cell: this.removeButton,
                                        }
                                    ]}
                                    className="-highlight ct-scroll"
                                    minRows={0}
                                    sortable={false}
                                    resizable
                                    filterable={false}
                                    loading={this.state.types === undefined}
                                    getTableProps={() => {
                                        return {
                                            style: {
                                                borderLeft: "0 none",
                                                borderRight: "0 none",
                                            }
                                        }
                                    }}
                                    getTrProps={(state, rowInfo, column) => {
                                        let index = rowInfo.index;
                                        let errorColor = `rgba(${colorMap['red']}, 0.15)`;
                                        let background = '';
                                        if (this.state.types[rowInfo.index]['$Error$']) {
                                            background = errorColor;
                                        } else if (index === this.state.selectedIndex) {
                                            background = 'rgba(63,144,63,0.15)';
                                        }

                                        return {
                                            style: {
                                                paddingTop: 5,
                                                height: rowInfo.original['$Error$'] ? 60 : 50,
                                                background,
                                            },
                                        };
                                    }}
                        />
                    </Col>
                    <Col md={2} xs={3}>
                        <FormGroup controlId="workflowUpDown">
                            <ControlLabel>Reorder</ControlLabel>
                            <Row>
                                <Button onClick={this.moveSelectedTypeUp}
                                        disabled={this.state.selectedIndex <= 0}
                                        block
                                        icon="menu-up"
                                        tooltip="Move selected record up"
                                />
                            </Row>
                            <Row>
                                <Button onClick={this.moveSelectedTypeDown}
                                        disabled={this.state.selectedIndex < 0 ||
                                                  this.state.selectedIndex === this.state.types.length - 1}
                                        block
                                        icon="menu-down"
                                        tooltip="Move selected record down"
                                />
                            </Row>
                            <Row>
                                <Button onClick={this.onAdd}
                                        disabled={this.state.loading || !enableAdd}
                                        style={{marginBottom: 15}}
                                        block
                                        icon="plus"
                                        iconStyle={{
                                            paddingBottom: '6px'
                                        }}
                                        label="Add"
                                        tooltip="Add new record"
                                />
                            </Row>
                        </FormGroup>
                    </Col>
                </Row>
                </Modal.Body>
                <Modal.Footer style={{ backgroundColor: dialogHeaderFooterBgColor }}>
                    <Button disabled={saveDisabled}
                            bsStyle="primary"
                            onClick={this.onSave}
                            icon="floppy-disk"
                            label="Save"
                    />
                </Modal.Footer>
            </Modal>
            );
    }
}

EditTypesDialog.propTypes = {
    types: PropTypes.array.isRequired,
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func,
    defaultSelectedIndex: PropTypes.int,
    label: PropTypes.string.isRequired,
    typeLabel: PropTypes.string.isRequired,
    loading: PropTypes.bool,
    modificationDisabled: PropTypes.func.isRequired,
    dispatchSaveAction: PropTypes.func,
    saveAction: PropTypes.func,
    defaultType: PropTypes.object.isRequired,
    enableAdd: PropTypes.bool,
    enableEdit: PropTypes.bool,
    enableDelete: PropTypes.bool,
}

export default connect()(EditTypesDialog);
